import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import type { CapitalDistribution } from "@/types";

export function useCapitalDistributions(fundId?: string) {
  return useQuery({
    queryKey: ["capital_distributions", fundId ?? "none"],
    enabled: !!fundId,
    queryFn: async () => {
      const { data } = await apiClient.get<CapitalDistribution[]>(
        `/funds/${fundId}/capital-distributions`
      );
      return data;
    },
  });
}

export function useCreateCapitalDistribution(fundId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { distribution_date: string; amount_brl: number }) => {
      const { data } = await apiClient.post<CapitalDistribution>(
        `/funds/${fundId}/capital-distributions`,
        payload
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["capital_distributions", fundId] }),
  });
}

export function useUpdateCapitalDistribution(fundId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: { id: string; distribution_date?: string; amount_brl?: number }) => {
      const { data } = await apiClient.patch<CapitalDistribution>(
        `/funds/${fundId}/capital-distributions/${id}`,
        payload
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["capital_distributions", fundId] }),
  });
}

export function useDeleteCapitalDistribution(fundId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/funds/${fundId}/capital-distributions/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["capital_distributions", fundId] }),
  });
}
